import { useEffect, useState } from "react";
import { fetchCmsPage, getCachedCmsPage } from "./appContentShellCache";

export function useCmsPage<T = unknown>(slug: string | undefined) {
  const [data, setData] = useState<T | null>(() => (slug ? getCachedCmsPage<T>(slug) : null));
  const [loading, setLoading] = useState<boolean>(() => !!slug && !getCachedCmsPage(slug));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setData(null);
      setLoading(false);
      return;
    }

    const cached = getCachedCmsPage<T>(slug);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    setLoading(true);
    setError(null);

    fetchCmsPage<T>(slug, controller.signal)
      .then(setData)
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err?.name === "AbortError") return;
        if (err?.name === "CanceledError") return;
        setError(err?.message ?? "Failed to load page");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [slug]);

  return { data, loading, error };
}